import React, { useMemo } from "react";
import {
  alpha,
  Autocomplete,
  Box,
  Chip,
  Grid2,
  Typography,
  useTheme,
} from "@mui/material";
import { useReportContext } from "../../../ReportContext/useReportContext";
import { EGCheckbox } from "../../../../MainComponents/Checkbox/EGCheckbox";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { useTranslation } from "react-i18next";
import { BoldFlag } from "../../../../Icon";
import Icon from "../../../../Icon/Icon";
import useLocalizedLabel from "../../../../../hooks/useLocalizedLabel";

export const IndicatorsAndTags = ({ filterValues, setFilterValues, tags }) => {
  // Custom hooks
  const { t, i18n } = useTranslation("report");
  const { indicatorOptions } = useReportContext();
  const theme = useTheme();
  const lang = i18n.language || "es";
  const getLocalizedLabel = useLocalizedLabel();

  // Tag mapping with translated labels
  const indicatorTags = useMemo(
    () =>
      Array.isArray(tags)
        ? tags.map((tag) => ({
            ...tag,
            label: getLocalizedLabel(tag),
            color: tag.key,
          }))
        : [],
    [tags, lang, getLocalizedLabel]
  );

  // Selected indicators
  const selectedIndicators = useMemo(
    () =>
      Array.isArray(indicatorOptions) && filterValues
        ? indicatorOptions.filter((option) => filterValues[option.key])
        : [],
    [indicatorOptions, filterValues]
  );

  // Selected tags (supports empty string without crashing)
  const selectedTags = useMemo(() => {
    const tagsArray = String(filterValues?.tags || "")
      .split(";")
      .filter(Boolean);
    return indicatorTags.filter((option) => tagsArray.includes(option.key));
  }, [indicatorTags, filterValues]);

  // Indicators handler
  const handleIndicatorsChange = (newSelectedOptions) => {
    const selectedKeys = newSelectedOptions.reduce((acc, option) => {
      acc[option.key] = true;
      return acc;
    }, {});

    setFilterValues((prev) => {
      const updated = { ...prev };
      indicatorOptions.forEach(({ key }) => {
        if (!selectedKeys[key]) {
          delete updated[key]; // Clears unselected
        }
      });
      return { ...updated, ...selectedKeys };
    });
  };

  // Tags handlers
  const handleTagsChange = (newSelectedOptions) => {
    const newTags = newSelectedOptions.map((option) => option.key).join(";");
    setFilterValues((prev) => ({
      ...prev,
      tags: newTags,
    }));
  };

  // Autocomplete
  const renderAutocomplete = ({
    options,
    selectedOptions,
    onChange,
    inputLabel,
    renderOptionIcon,
    chipBackgroundColor,
  }) => (
    <Autocomplete
      multiple
      options={options}
      getOptionLabel={(option) => option.label}
      value={selectedOptions}
      onChange={(_, newValue) => onChange(newValue)}
      disableCloseOnSelect
      renderOption={({ key, ...props }, option, { selected }) => (
        <Box
          key={key}
          component="li"
          {...props}
          sx={{
            maxHeight: { xs: 35, md: 40 },
            p: 1,
            mx: 1,
            borderRadius: 1.5,
            border: `${theme.palette.blueGray50.main} 1px solid`,
          }}
        >
          <EGCheckbox size="small" checked={selected} color="primary" />
          {renderOptionIcon(option)}
          <Typography variant="body2" mx={1}>
            {option.label}
          </Typography>
        </Box>
      )}
      renderInput={(params) => <EGInput {...params} label={inputLabel} />}
      renderTags={(tagValue, getTagProps) => (
        <Box
          sx={{
            maxHeight: { xs: "15vh", md: "20vh" },
            minHeight: "30px",
            overflowY: "auto",
            overflowX: "hidden",
            mr: 1,
            my: 0.33,
            gap: 0.3,
            pointerEvents: "none",
          }}
        >
          {tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.label}
              sx={{
                backgroundColor: chipBackgroundColor(option),
                px: { xs: 0, md: 0.5 },
                mr: 2,
                pointerEvents: "auto",
                height: 28,
                fontSize: 12,
              }}
              label={
                <Typography
                  variant="caption"
                  fontSize={12}
                  fontWeight={600}
                  mx={{ xs: 0.5, md: 1 }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {option.label}
                </Typography>
              }
              size="small"
              icon={renderOptionIcon(option)}
            />
          ))}
        </Box>
      )}
    />
  );

  return (
    <Grid2
      container
      spacing={1}
      sx={{
        width: "100%",
        mt: 1,
        px: 0.5,
      }}
    >
      {/* Indicators */}
      <Grid2 size={{ xs: 12, sm: 6 }}>
        {renderAutocomplete({
          options: indicatorOptions,
          selectedOptions: selectedIndicators,
          onChange: handleIndicatorsChange,
          inputLabel: `${t("Filter.indicators_selection")}:`,
          renderOptionIcon: (option) => (
            <Icon
              name={option.iconName}
              color={option.color || theme.palette.primary.main}
              size={18}
            />
          ),
          chipBackgroundColor: () => theme.palette.blueGray50.main,
        })}
      </Grid2>
      {/* Tags */}
      <Grid2 size={{ xs: 12, sm: 6 }}>
        {renderAutocomplete({
          options: indicatorTags,
          selectedOptions: selectedTags,
          onChange: handleTagsChange,
          inputLabel: `${t("Filter.classification_tags")}:`,
          renderOptionIcon: (option) => (
            <BoldFlag
              size={18}
              color={
                theme.palette?.[option.color]?.main ||
                theme.palette.primary.main
              }
            />
          ),
          chipBackgroundColor: (option) =>
            alpha(
              theme.palette?.[option.color]?.main || theme.palette.primary.main,
              0.1
            ),
        })}
      </Grid2>
    </Grid2>
  );
};
